import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IRowNode,
	MenuItemDef,
} from "ag-grid-community";

import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { useNavigate } from "react-router-dom";
import { CatalogAdjustmentReason } from "../../../models/CatalogAdjustmentReason";
import {
	reasonUrl,
	useAdjustmentReasons,
} from "../CatalogAdjustmentReasonPage";
import { useAdjustmentReasonOptionsFunctions } from "../hooks/useAdjustmentReasonOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";

export const TableAdjustmentReason = ({
	reasonId,
}: {
	reasonId: string | undefined;
}) => {
	const navigate = useNavigate();

	const [filterValue, setFilterValue] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [selectedRowData, setSelectedRowData] =
		useState<CatalogAdjustmentReason | null>(null);
	const [error, setError] = useState("");

	const { data: rows, isLoading } = useAdjustmentReasons((error) =>
		setError(error.message),
	);

	const handleNewReason = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setSelectedRowData(null);
		navigate(`${reasonUrl}new`);
	};

	const editAdjustmentReason = (data: CatalogAdjustmentReason) => {
		setSelectedRowData(data);
		setShowModal("update");
		navigate(`${reasonUrl}${data.AdjustmentReasonId}`);
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useAdjustmentReasonOptionsFunctions({
		setSelectedRowData,
		setShowModal,
		handleNewReason,
		editAdjustmentReason,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowDoubleClicked = (event: any) => {
		setSelectedRowData(event.data as CatalogAdjustmentReason);
		setShowModal("update");
		navigate(
			`${reasonUrl}${
				(event.data as CatalogAdjustmentReason).AdjustmentReasonId
			}`,
		);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "adjustmentReasonContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	const onModalHide = (id?: number) => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("");
		navigate(reasonUrl);
	};

	useEffect(() => {
		if (!isLoading) {
			if (reasonId) {
				if (reasonId === "new") {
					handleNewReason();
				} else {
					let reasonNode: IRowNode;

					gridApi?.forEachNode((n) => {
						if (
							(n.data as CatalogAdjustmentReason).AdjustmentReasonId ===
							Number(reasonId)
						) {
							reasonNode = n;
							return true;
						}
					});

					if (reasonNode!) {
						setFilterValue(
							(reasonNode.data as CatalogAdjustmentReason).AdjustmentReasonName,
						);
						gridApi?.setGridOption(
							"quickFilterText",
							(reasonNode.data as CatalogAdjustmentReason).AdjustmentReasonName,
						);

						gridApi?.ensureNodeVisible(reasonNode);
						reasonNode.setSelected(true);
						setSelectedRowData(reasonNode.data);
						setShowModal("update");
					}
				}
			}
		}
	}, [isLoading, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "adjustmentReasonContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<Grid2 container justifyContent="center" p={1} spacing={1}>
			<Grid2 size={{ md: 12, xs: 12 }}>
				<Typography variant="h5" fontWeight={600}>
					Adjustment Reasons
				</Typography>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 12 }}>
				<TextFieldSearchFilter
					value={filterValue}
					onChange={(val) => {
						setFilterValue(val);
						gridApi?.setGridOption("quickFilterText", val);
					}}
				/>
			</Grid2>
			<Grid2 container justifyContent="center" alignItems="center">
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper elevation={2} style={{ height: "64vh", width: "100%" }}>
						<AgGridReact
							rowData={rows || []}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={34}
							animateRows
							getContextMenuItems={getContextMenuItems}
							onRowDoubleClicked={onRowDoubleClicked}
							onGridReady={(params) => setGridApi(params.api)}
							getRowId={(params) =>
								String(
									(params.data as CatalogAdjustmentReason).AdjustmentReasonId,
								)
							}
							loadingOverlayComponent={CenteredLazyLoading}
							gridOptions={{
								rowSelection: {
									mode: "singleRow",
									checkboxes: false,
									enableClickSelection: true,
								},
							}}
						/>
					</Paper>
				</Grid2>
				<FooterButton
					label="ADJUSTMENT REASON"
					showCreateModal={showModal === "create"}
					setShowCreateModal={(showCreateModal) =>
						showCreateModal ? handleNewReason() : setShowModal("")
					}
					sizes={{ md: 3 }}
				/>
			</Grid2>
			<UpsertDeleteModal
				adjustmentReason={selectedRowData}
				onHide={onModalHide}
				modal={showModal}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Grid2>
	);
};
